import { api } from "#/lib/api-client";
import type {
  AirtimeAllocation,
  AirtimeBatch,
  AirtimeConfig,
  AirtimeDistribution,
  AirtimeStats,
  PageResponse,
} from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.number === 0,
    last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? 1),
  };
}

export const airtimeApi = {
  async getStats(): Promise<AirtimeStats> {
    const { data } = await api.get<AirtimeStats>("/api/hr/airtime/stats");
    return data;
  },

  async getConfig(): Promise<AirtimeConfig> {
    const { data } = await api.get<AirtimeConfig>("/api/hr/airtime/config");
    return data;
  },

  async updateConfig(payload: Partial<AirtimeConfig>): Promise<AirtimeConfig> {
    const { data } = await api.put<AirtimeConfig>(
      "/api/hr/airtime/config",
      payload,
    );
    return data;
  },

  async listAllocations(params: {
    page: number;
    size: number;
    search?: string;
  }): Promise<PageResponse<AirtimeAllocation>> {
    const { data } = await api.get(
      "/api/hr/airtime/allocations",
      { params },
    );
    return normalizePage<AirtimeAllocation>(data);
  },

  async updateAllocation(
    employeeId: number,
    amount: number,
  ): Promise<AirtimeAllocation> {
    const { data } = await api.put<AirtimeAllocation>(
      `/api/hr/airtime/allocations/${employeeId}`,
      { amount },
    );

    return data;
  },

  async getMyAllocation(): Promise<AirtimeAllocation> {
    const { data } = await api.get<AirtimeAllocation>(
      "/api/hr/airtime/my-allocation",
    );
    return data;
  },

  async updateMyDistribution(
    payload: AirtimeDistribution[],
  ): Promise<AirtimeDistribution[]> {
    const { data } = await api.put<AirtimeDistribution[]>(
      "/api/hr/airtime/my-distribution",
      payload.map((row) => ({
        phoneNumber: row.phoneNumber,
        amount: Number(row.amount),
      })),
    );
    return data;
  },

  async listBatches(params: {
    page: number;
    size: number;
  }): Promise<PageResponse<AirtimeBatch>> {
    const { data } = await api.get(
      "/api/hr/airtime/batches",
      { params },
    );
    return normalizePage<AirtimeBatch>(data);
  },

  async runDisbursement(): Promise<AirtimeBatch> {
    const { data } = await api.post<AirtimeBatch>(
      "/api/hr/airtime/disbursements/run",
    );
    return data;
  },
};
